import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  Button,
  Alert,
  AlertIcon,
  HStack,
  Badge,
  useToast,
  Spinner
} from '@chakra-ui/react';
import { CheckIcon } from '@chakra-ui/icons';

interface EmbeddedSignatureFlowProps {
  documentType: 'W9_FORM' | 'ACH_AUTHORIZATION' | 'VENDOR_AGREEMENT';
  vendorData: any;
  signerInfo: {
    name: string;
    email: string;
  };
  onComplete: (signatureRequestId: string) => void;
  onError: (error: string) => void;
  isActive: boolean;
}

interface SignatureResponse {
  success: boolean;
  signatureRequestId: string;
  embeddedSignUrl: string;
  expiresAt: string;
  templateType: string;
  documentType: string;
  error?: string;
}

const BACKEND_API_URL = process.env.REACT_APP_API_URL || 
  'https://vendor-portal-backend.yellowforest-ecf59216.westus.azurecontainerapps.io/api';

const EmbeddedSignatureFlow: React.FC<EmbeddedSignatureFlowProps> = ({
  documentType,
  vendorData,
  signerInfo,
  onComplete,
  onError,
  isActive
}) => {
  const [loading, setLoading] = useState(false);
  const [signatureData, setSignatureData] = useState<SignatureResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const toast = useToast();
  // Removed unused disclosure hooks

  // Document type display mapping
  const documentTitles = {
    W9_FORM: 'W-9 Tax Form',
    ACH_AUTHORIZATION: 'ACH Authorization Form',
    VENDOR_AGREEMENT: 'Vendor Agreement Terms'
  };

  useEffect(() => {
    if (isActive && !signatureData && !loading) {
      createSignatureRequest();
    }
  }, [isActive, signatureData, loading]);

  // Listen for DocuSign events and completion
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Handle both DocuSign and general iframe events
      console.log('📨 Iframe Event:', event.data, 'Origin:', event.origin);
      
      // DocuSign completion handling - DocuSign uses different event patterns
      if (event.data.type === 'docusign_signing_complete' || 
          event.data.type === 'signing_complete' ||
          (typeof event.data === 'string' && event.data.includes('signed'))) {
        console.log('✅ Document signed successfully via event!');
        setIsComplete(true);
        
        toast({
          title: 'Document Signed!',
          description: `${documentTitles[documentType]} has been signed successfully.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        if (signatureData) {
          onComplete(signatureData.signatureRequestId);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [documentType, signatureData, onComplete, onError, toast, documentTitles]);

  // Monitor iframe for completion via URL detection
  useEffect(() => {
    if (!signatureData || isComplete) return;

    const checkForCompletion = () => {
      try {
        // Simple completion detection - if iframe stays on the embedded URL for a while, 
        // it likely means signing is in progress. If it redirects, it's likely complete.
        if (iframeRef.current) {
          const iframe = iframeRef.current;
          // This is a simple heuristic - if the iframe has been loaded for a while
          // and we get a specific message or timeout, consider it potentially complete
        }
      } catch (error) {
        // Cross-origin restrictions, this is expected
      }
    };

    const intervalId = setInterval(checkForCompletion, 5000);
    return () => clearInterval(intervalId);
  }, [signatureData, isComplete]);

  const createSignatureRequest = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log(`🎯 Creating signature request for ${documentType}`);



      // Use the working signature engine endpoint
      const endpoint = 'signature-engine/create-signature';

      // Use the working backend's expected format
      const requestData = {
        documentType: documentType,
        vendorData: vendorData,
        signerInfo: signerInfo
      };

      const response = await fetch(`${BACKEND_API_URL}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to create signature request');
      }

      // The working backend already returns the correct format
      const mappedData: SignatureResponse = {
        success: data.success,
        signatureRequestId: data.signatureRequestId,
        embeddedSignUrl: data.embeddedSignUrl,
        expiresAt: data.expiresAt,
        templateType: data.templateType,
        documentType: data.documentType
      };

      console.log('✅ Signature request created:', mappedData.signatureRequestId);
      setSignatureData(mappedData);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create signature request';
      console.error('❌ Signature creation failed:', errorMessage);
      setError(errorMessage);
      onError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const retrySignature = () => {
    setSignatureData(null);
    setError(null);
    setIsComplete(false);
    createSignatureRequest();
  };

  if (loading) {
    return (
      <Box p={6} textAlign="center">
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" />
          <Text>Creating {documentTitles[documentType]}...</Text>
          <Text fontSize="sm" color="gray.600">
            Preparing embedded signature experience
          </Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <VStack align="start" spacing={3} flex={1}>
            <Text fontWeight="bold">Signature System Deployment In Progress</Text>
            <Text fontSize="sm">
              We're currently deploying the enhanced signature integration. In the meantime, you can continue with your registration and we'll contact you to complete the signatures.
            </Text>
            <VStack align="start" spacing={2} fontSize="sm" color="gray.600">
              <Text>• Your registration has been saved</Text>
              <Text>• We'll email you the documents for signature</Text>
              <Text>• No action needed on your part right now</Text>
            </VStack>
            <HStack spacing={3}>
              <Button size="sm" colorScheme="blue" variant="outline" onClick={retrySignature}>
                Try Again
              </Button>
              <Button size="sm" colorScheme="green" onClick={() => onComplete('manual-fallback')}>
                Continue Registration
              </Button>
            </HStack>
          </VStack>
        </Alert>
      </Box>
    );
  }

  if (isComplete) {
    return (
      <Box p={6} textAlign="center">
        <VStack spacing={4}>
          <CheckIcon color="green.500" boxSize={8} />
          <Text fontSize="lg" fontWeight="bold" color="green.600">
            Document Signed Successfully!
          </Text>
          <Text color="gray.600">
            {documentTitles[documentType]} has been completed
          </Text>
          <Badge colorScheme="green" variant="solid" p={2}>
            Signature Request: {signatureData?.signatureRequestId.substring(0, 8)}...
          </Badge>
        </VStack>
      </Box>
    );
  }

  if (!signatureData) {
    return (
      <Box p={6} textAlign="center">
        <Alert status="warning">
          <AlertIcon />
          <Text>No signature data available. Please try again.</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Instructions */}
      <Text fontSize="sm" color="gray.600" mb={4}>
        Please review and sign the document below. The form has been pre-filled with your information.
      </Text>

      {/* Embedded Signature Frame */}
      <Box 
        border="2px solid" 
        borderColor="gray.200" 
        borderRadius="lg" 
        overflow="hidden"
        bg="white"
        boxShadow="lg"
      >
        <iframe
          ref={iframeRef}
          src={signatureData.embeddedSignUrl}
          width="100%"
          height="800px"
          frameBorder="0"
          allowFullScreen
          allow="camera; microphone; fullscreen; autoplay; encrypted-media"
          title={`Sign ${documentTitles[documentType]}`}
          style={{
            border: 'none',
            borderRadius: '8px',
            minHeight: '800px'
          }}
        />
      </Box>

      {/* Footer Info */}
      <VStack spacing={2} mt={4} align="start">
        <HStack spacing={4}>
          <Text fontSize="xs" color="gray.500">
            Signature ID: {signatureData.signatureRequestId.substring(0, 12)}...
          </Text>
          <Text fontSize="xs" color="gray.500">
            Expires: {new Date(signatureData.expiresAt).toLocaleDateString()}
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          🔒 Secured by Dropbox Sign - Your information is encrypted and protected
        </Text>
      </VStack>
    </Box>
  );
};

export default EmbeddedSignatureFlow; 